import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BookOpen, Loader2, Printer, Wallet } from 'lucide-react'

import { ZReportPrint } from '@/components/accounts/ZReportPrint'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  closeCashShift,
  createJournalVoucher,
  fetchCashShifts,
  fetchChartOfAccounts,
  fetchDayBook,
  fetchOpenCashShift,
  fetchZReports,
  formatLedgerMoney,
  journalLineTotals,
  openCashShift,
} from '@/lib/api/accounts'
import {
  formatZReportDateTime,
  normalizePaymentMix,
  printZReport,
} from '@/lib/accounts/zReport'
import { cn } from '@/lib/utils'

const TABS = [
  { id: 'shift', label: 'Cash shift' },
  { id: 'z_report', label: 'Z-report' },
  { id: 'accounts', label: 'Chart of accounts' },
  { id: 'journal', label: 'Journal entry' },
]

const inputClass = cn(
  'h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

const emptyJournalLine = () => ({
  chart_of_account_id: '',
  debit: '',
  credit: '',
})

export function AccountsPage() {
  const { activeBranchId, activeBranch } = useBranch()
  const zReportPrintRef = useRef(null)
  const [tab, setTab] = useState('shift')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [accounts, setAccounts] = useState([])
  const [openShift, setOpenShift] = useState(null)
  const [shifts, setShifts] = useState([])
  const [zReports, setZReports] = useState([])
  const [selectedZReportId, setSelectedZReportId] = useState(null)
  const [dayBook, setDayBook] = useState({ entries: [] })

  const [openingFloat, setOpeningFloat] = useState('500')
  const [closeForm, setCloseForm] = useState({ closing_cash_counted: '', notes: '' })
  const [journalForm, setJournalForm] = useState({
    voucher_date: new Date().toISOString().slice(0, 10),
    narration: '',
    lines: [emptyJournalLine(), emptyJournalLine()],
  })

  const journalTotals = useMemo(
    () => journalLineTotals(journalForm.lines),
    [journalForm.lines],
  )

  const selectedZReport = useMemo(
    () => zReports.find((report) => report.id === selectedZReportId) ?? zReports[0] ?? null,
    [selectedZReportId, zReports],
  )

  const selectedPaymentMix = useMemo(
    () => normalizePaymentMix(selectedZReport?.payment_mix),
    [selectedZReport],
  )

  const reload = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [accountItems, currentShift, shiftItems, reportItems, book] = await Promise.all([
        fetchChartOfAccounts(),
        fetchOpenCashShift(),
        fetchCashShifts(),
        fetchZReports(),
        fetchDayBook(),
      ])
      setAccounts(accountItems)
      setOpenShift(currentShift)
      setShifts(shiftItems)
      setZReports(reportItems)
      setSelectedZReportId((current) => {
        if (current && reportItems.some((report) => report.id === current)) {
          return current
        }
        return reportItems[0]?.id ?? null
      })
      setDayBook(book)
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to load accounts.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload, activeBranchId])

  async function handleOpenShift(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')
    try {
      await openCashShift(Number(openingFloat))
      setSuccess('Cash shift opened.')
      await reload()
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to open shift.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleCloseShift(event) {
    event.preventDefault()
    if (!openShift?.id) return

    setSubmitting(true)
    setError('')
    setSuccess('')
    try {
      const result = await closeCashShift(openShift.id, {
        closing_cash_counted: Number(closeForm.closing_cash_counted),
        notes: closeForm.notes || undefined,
      })
      setSuccess(`Shift ${result.cash_shift?.shift_number ?? ''} closed. Z-report generated.`)
      setCloseForm({ closing_cash_counted: '', notes: '' })
      if (result.z_report?.id) {
        setSelectedZReportId(result.z_report.id)
        setTab('z_report')
      }
      await reload()
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to close shift.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleJournalSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')
    try {
      const voucher = await createJournalVoucher({
        voucher_date: journalForm.voucher_date,
        narration: journalForm.narration,
        lines: journalForm.lines
          .filter((line) => line.chart_of_account_id)
          .map((line) => ({
            chart_of_account_id: Number(line.chart_of_account_id),
            debit: line.debit ? Number(line.debit) : 0,
            credit: line.credit ? Number(line.credit) : 0,
          })),
      })
      setSuccess(`Journal ${voucher.voucher_number} posted.`)
      setJournalForm({
        voucher_date: new Date().toISOString().slice(0, 10),
        narration: '',
        lines: [emptyJournalLine(), emptyJournalLine()],
      })
      await reload()
      setTab('journal')
    } catch (err) {
      const message = err?.response?.data?.message
      const lineErrors = err?.response?.data?.errors
      setError(message ?? lineErrors?.lines?.[0] ?? 'Journal posting failed.')
    } finally {
      setSubmitting(false)
    }
  }

  function updateJournalLine(index, field, value) {
    setJournalForm((current) => ({
      ...current,
      lines: current.lines.map((line, lineIndex) => (
        lineIndex === index ? { ...line, [field]: value } : line
      )),
    }))
  }

  return (
    <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 lg:px-8">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Wallet className="h-5 w-5" aria-hidden />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Accounts</h1>
          <p className="text-sm text-muted-foreground">
            Cash shift control, Z-reports, chart of accounts, and manual journal vouchers.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={cn(
              'rounded-xl px-3 py-1.5 text-sm font-medium transition-colors',
              tab === item.id ? 'bg-primary text-primary-foreground' : 'bg-muted/70 hover:bg-muted',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {success ? <p className="text-sm text-emerald-600">{success}</p> : null}

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading accounts…
        </div>
      ) : null}

      {!loading && tab === 'shift' ? (
        <div className="grid gap-6 lg:grid-cols-2">
          {openShift ? (
            <form onSubmit={handleCloseShift} className="card-surface space-y-4 p-6">
              <h2 className="font-semibold">Close open shift</h2>
              <p className="text-sm text-muted-foreground">
                {openShift.shift_number} · opened {openShift.opened_at ? new Date(openShift.opened_at).toLocaleString() : '—'}
              </p>
              <p className="text-sm">
                Opening float: <strong>{formatLedgerMoney(openShift.opening_float)}</strong>
              </p>
              <input
                type="number"
                min="0"
                step="0.01"
                required
                placeholder="Counted cash in drawer"
                value={closeForm.closing_cash_counted}
                onChange={(e) => setCloseForm((f) => ({ ...f, closing_cash_counted: e.target.value }))}
                className={inputClass}
              />
              <textarea
                placeholder="Notes (optional)"
                value={closeForm.notes}
                onChange={(e) => setCloseForm((f) => ({ ...f, notes: e.target.value }))}
                className={cn(inputClass, 'min-h-[80px] py-2')}
              />
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Closing…' : 'Close shift & generate Z-report'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleOpenShift} className="card-surface space-y-4 p-6">
              <h2 className="font-semibold">Open cash shift</h2>
              <p className="text-sm text-muted-foreground">Record the opening float in the cash drawer.</p>
              <input
                type="number"
                min="0"
                step="0.01"
                required
                value={openingFloat}
                onChange={(e) => setOpeningFloat(e.target.value)}
                className={inputClass}
              />
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Opening…' : 'Open shift'}
              </Button>
            </form>
          )}

          <section className="card-surface overflow-x-auto p-4">
            <h2 className="mb-3 font-semibold">Recent shifts</h2>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-2 py-2">Shift</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">Opened</th>
                  <th className="px-2 py-2">Variance</th>
                </tr>
              </thead>
              <tbody>
                {shifts.map((shift) => (
                  <tr key={shift.id} className="border-b border-border/40">
                    <td className="px-2 py-2 font-medium">{shift.shift_number}</td>
                    <td className="px-2 py-2 capitalize">{shift.status}</td>
                    <td className="px-2 py-2 text-muted-foreground">
                      {shift.opened_at ? new Date(shift.opened_at).toLocaleString() : '—'}
                    </td>
                    <td className={cn('px-2 py-2', Number(shift.cash_variance) !== 0 && 'font-semibold text-amber-600')}>
                      {shift.cash_variance != null ? formatLedgerMoney(shift.cash_variance) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      ) : null}

      {!loading && tab === 'z_report' ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <section className="card-surface overflow-x-auto p-4">
            <h2 className="mb-3 font-semibold">Recent Z-reports</h2>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-2 py-2">Report</th>
                  <th className="px-2 py-2">Date</th>
                  <th className="px-2 py-2">Gross</th>
                  <th className="px-2 py-2">Variance</th>
                </tr>
              </thead>
              <tbody>
                {zReports.map((report) => (
                  <tr
                    key={report.id}
                    className={cn(
                      'cursor-pointer border-b border-border/40',
                      selectedZReport?.id === report.id && 'bg-primary/5',
                    )}
                    onClick={() => setSelectedZReportId(report.id)}
                  >
                    <td className="px-2 py-2 font-medium">{report.report_number}</td>
                    <td className="px-2 py-2 text-muted-foreground">{report.business_date}</td>
                    <td className="px-2 py-2">{formatLedgerMoney(report.gross_sales)}</td>
                    <td className={cn('px-2 py-2', Number(report.cash_variance) !== 0 && 'font-semibold text-amber-600')}>
                      {formatLedgerMoney(report.cash_variance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {zReports.length === 0 ? (
              <p className="px-2 py-4 text-sm text-muted-foreground">Close a cash shift to generate a Z-report.</p>
            ) : null}
          </section>

          {selectedZReport ? (
            <section className="card-surface space-y-4 p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-semibold">{selectedZReport.report_number}</h2>
                  <p className="text-sm text-muted-foreground">
                    {selectedZReport.business_date} · generated {formatZReportDateTime(selectedZReport.generated_at)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => printZReport(zReportPrintRef.current)}
                >
                  <Printer className="mr-2 h-4 w-4" />
                  Print Z-report
                </Button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <Metric label="Gross sales" value={formatLedgerMoney(selectedZReport.gross_sales)} />
                <Metric label="Net sales" value={formatLedgerMoney(selectedZReport.net_sales)} />
                <Metric label="VAT" value={formatLedgerMoney(selectedZReport.vat_total)} />
                <Metric label="Discounts" value={formatLedgerMoney(selectedZReport.discount_total)} />
                <Metric label="Voids" value={`${selectedZReport.void_count ?? 0} / ${formatLedgerMoney(selectedZReport.void_amount)}`} />
                <Metric label="Opening float" value={formatLedgerMoney(selectedZReport.opening_float)} />
                <Metric label="Expected cash" value={formatLedgerMoney(selectedZReport.closing_cash_expected)} />
                <Metric label="Counted cash" value={formatLedgerMoney(selectedZReport.closing_cash_counted)} />
                <Metric
                  label="Cash variance"
                  value={formatLedgerMoney(selectedZReport.cash_variance)}
                  highlight={Number(selectedZReport.cash_variance) !== 0}
                />
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold">Payment mix</h3>
                <table className="min-w-full text-sm">
                  <tbody>
                    {selectedPaymentMix.map((line) => (
                      <tr key={line.code} className="border-b border-border/40">
                        <td className="px-2 py-2">{line.label}</td>
                        <td className="px-2 py-2 text-right font-medium">{line.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
        </div>
      ) : null}

      {!loading && tab === 'accounts' ? (
        <section className="card-surface overflow-x-auto p-4">
          <div className="mb-3 flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-semibold">Chart of accounts</h2>
          </div>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-2 py-2">Code</th>
                <th className="px-2 py-2">Name</th>
                <th className="px-2 py-2">Type</th>
                <th className="px-2 py-2">System</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id} className="border-b border-border/40">
                  <td className="px-2 py-2 font-mono">{account.code}</td>
                  <td className="px-2 py-2">{account.name}</td>
                  <td className="px-2 py-2 capitalize">{account.account_type}</td>
                  <td className="px-2 py-2">{account.is_system ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {!loading && tab === 'journal' ? (
        <div className="grid gap-6 lg:grid-cols-2">
          <form onSubmit={handleJournalSubmit} className="card-surface space-y-4 p-6">
            <h2 className="font-semibold">New journal voucher</h2>
            <input
              type="date"
              required
              value={journalForm.voucher_date}
              onChange={(e) => setJournalForm((f) => ({ ...f, voucher_date: e.target.value }))}
              className={inputClass}
            />
            <input
              type="text"
              required
              placeholder="Narration"
              value={journalForm.narration}
              onChange={(e) => setJournalForm((f) => ({ ...f, narration: e.target.value }))}
              className={inputClass}
            />
            {journalForm.lines.map((line, index) => (
              <div key={index} className="grid gap-2 md:grid-cols-4">
                <select
                  required
                  value={line.chart_of_account_id}
                  onChange={(e) => updateJournalLine(index, 'chart_of_account_id', e.target.value)}
                  className={cn(inputClass, 'md:col-span-2')}
                >
                  <option value="">Account</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.code} — {account.name}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Debit"
                  value={line.debit}
                  onChange={(e) => updateJournalLine(index, 'debit', e.target.value)}
                  className={inputClass}
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Credit"
                  value={line.credit}
                  onChange={(e) => updateJournalLine(index, 'credit', e.target.value)}
                  className={inputClass}
                />
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setJournalForm((f) => ({ ...f, lines: [...f.lines, emptyJournalLine()] }))}
              >
                Add line
              </Button>
              <p className={cn('text-sm', journalTotals.balanced ? 'text-emerald-600' : 'text-amber-600')}>
                Debit {formatLedgerMoney(journalTotals.debit)} · Credit {formatLedgerMoney(journalTotals.credit)}
              </p>
            </div>
            <Button type="submit" disabled={submitting || !journalTotals.balanced}>
              {submitting ? 'Posting…' : 'Post journal'}
            </Button>
          </form>

          <section className="card-surface overflow-x-auto p-4">
            <h2 className="mb-3 font-semibold">Day book ({dayBook.from} → {dayBook.to})</h2>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-2 py-2">Date</th>
                  <th className="px-2 py-2">Voucher</th>
                  <th className="px-2 py-2">Account</th>
                  <th className="px-2 py-2">Debit</th>
                  <th className="px-2 py-2">Credit</th>
                </tr>
              </thead>
              <tbody>
                {(dayBook.entries ?? []).map((entry, index) => (
                  <tr key={`${entry.voucher_id}-${entry.account_code}-${index}`} className="border-b border-border/40">
                    <td className="px-2 py-2 text-muted-foreground">{entry.voucher_date}</td>
                    <td className="px-2 py-2">{entry.voucher_number}</td>
                    <td className="px-2 py-2">{entry.account_code} {entry.account_name}</td>
                    <td className="px-2 py-2">{entry.debit}</td>
                    <td className="px-2 py-2">{entry.credit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      ) : null}

      <div ref={zReportPrintRef}>
        <ZReportPrint report={selectedZReport} branch={activeBranch} />
      </div>
    </div>
  )
}

/**
 * @param {object} props
 * @param {string} props.label
 * @param {string} props.value
 * @param {boolean} [props.highlight]
 */
function Metric({ label, value, highlight = false }) {
  return (
    <div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={cn('text-sm font-semibold', highlight && 'text-amber-600')}>{value}</p>
    </div>
  )
}
